import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class DeeplinkService {
    private readonly DEEPLINK_KEY = 'deeplink';

    constructor() { }

    /**
     * Store deep link context in session storage
     * @param url The initial URL that the user accessed
     * @param queryParams Any query parameters from the URL
     */
    storeDeeplinkContext(url: string, queryParams: any = {}): void {
        const context = {
            url,
            queryParams,
            timestamp: new Date().toISOString()
        };
        sessionStorage.setItem(this.DEEPLINK_KEY, JSON.stringify(context));
    }

    /**
     * Get the stored deep link context
     * @returns The deep link context or null if not found
     */
    getDeeplinkContext(): { url: string; queryParams: any; timestamp: string } | null {
        const stored = sessionStorage.getItem(this.DEEPLINK_KEY);
        if (stored) {
            try {
                return JSON.parse(stored);
            } catch (error) {
                return null;
            }
        }
        return null;
    }

    /**
     * Check if there's an active deep link context
     * @returns true if deep link context exists
     */
    hasDeeplinkContext(): boolean {
        return sessionStorage.getItem(this.DEEPLINK_KEY) !== null;
    }

    /**
     * Clear the deep link context from session storage
     * Should be called after the deep link action is completed (e.g., after booking confirmation)
     */
    clearDeeplinkContext(): void {
        sessionStorage.removeItem(this.DEEPLINK_KEY);
    }

    /**
     * Check if the current route should skip authentication based on deep link context
     * @param currentUrl The current route URL
     * @returns true if the route should allow anonymous access
     */
    shouldSkipAuth(currentUrl: string): boolean {
        const context = this.getDeeplinkContext();
        if (!context) {
            return false;
        }

        // Check if the stored URL matches or is related to the current URL
        // This allows page reloads to preserve the deep link context
        return context.url.includes(currentUrl) || currentUrl.includes(context.url);
    }
}
